import React, { useState } from "react";
import {
  ChevronDown,
  Link as LinkIcon,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { FILE_BASE_URL } from "../api/fileServer";
import ReviewerReviews from "./ReviewerReviews";

export default function QuestionCard({
  q,
  onMarks,
  onOptionMarks,
  onVerify,
  onRemark,
  isSuperAdmin = false,
}) {

  const [open, setOpen] = useState(true);

  const verified = Boolean(q.isVerified);

  const pillClass = verified
    ? "bg-emerald-50 text-emerald-600"
    : "bg-red-50 text-red-600";

  const pillLabel = verified
    ? "✔ Verified"
    : "✖ Pending";

  const files = q.files ?? [];
  const options = q.options ?? [];

  const isSection24 = Number(q.sectionNo) === 24;

  // Teacher's original marks
  const teacherMarks = Number(
    q.teacherMarks ??
    q.marks ??
    q.obtainedMarks ??
    0
  );

  // Reviewed marks
  const reviewedMarks = Number(
    q.reviewedMarks ??
    q.reviewedMarksObtained ??
    0
  );

  const reviewerReviews = Array.isArray(q.reviewerReviews)
    ? q.reviewerReviews
    : [];

  // Section 24 MUST always show 5 rows.
  // If backend has fewer than 5 options, create empty rows.
  const section24Options = isSection24
    ? Array.from({ length: 5 }, (_, index) => {
      return (
        options[index] ?? {
          optionId: `section24-empty-${index + 1}`,
          selectedOptionId: null,
          selectedOption: null,
          reviewedMarks: 0,
          maxMarks: 5,
          displayOrder: index + 1,
          isEmpty: true,
        }
      );
    })
    : options;

  const displayOptions = isSection24
    ? Array.from({ length: 5 }, (_, index) => {
      const existingOption = options[index];

      if (existingOption) {
        return {
          ...existingOption,
          isEmpty: false
        };
      }

      // DISPLAY ONLY.
      // This does NOT represent a real backend option.
      return {
        optionId: null,
        selectedOptionId: null,
        selectedOption: null,
        reviewedMarks: 0,
        maxMarks: 5,
        displayOrder: index + 1,
        isEmpty: true
      };
    })
    : options;
  /*
   * Raw option-wise total.
   *
   * Example:
   *
   * 5 + 5 + 5 + 5 + 5 = 25
   */
  const optionMarksTotal = displayOptions.reduce(
    (sum, option) =>
      sum + Number(option.reviewedMarks ?? 0),
    0
  );

  const sectionReviewedMarks =
    q.sectionNo === 24
      ? Math.min(q.maxMarks, optionMarksTotal)
      : Number(q.reviewedMarks ?? 0);

  /*
   * Section 24 can only contribute
   * maximum 5 marks.
   */
  const section24ReviewedMarks = isSection24
    ? Math.min(
      Number(q.maxMarks ?? 5),
      optionMarksTotal
    )
    : reviewedMarks;

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">

      {/* HEADER */}
      <div
        className="flex items-center gap-3 px-5 py-4 cursor-pointer"
        onClick={() => setOpen(!open)}
      >

        <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center font-bold">
          {q.sectionNo}
        </div>

        <div className="flex-1">

          <div className="font-semibold">
            {q.title}
          </div>

          {q.subtitle && (
            <div className="text-xs text-slate-500">
              {q.subtitle}
            </div>
          )}

        </div>

        <div className="text-xs bg-slate-100 rounded px-3 py-1">
          {q.maxMarks} Marks
        </div>

        <span
          className={`text-xs px-3 py-1 rounded-full ${pillClass}`}
        >
          {pillLabel}
        </span>

        <ChevronDown
          size={18}
          className={open ? "rotate-180" : ""}
        />

      </div>

      {/* BODY */}
      {open && (
        <div className="border-t p-5">

          {/* UPLOADED FILES */}
          {/* UPLOADED FILES */}

          <h4 className="font-semibold mb-3">
            Uploaded Files
          </h4>

          {files.length === 0 ? (
            <div className="text-sm text-slate-400">
              No files uploaded.
            </div>
          ) : (
            <div className="grid gap-3">

              {files.map((file, index) => {

                const fileId =
                  file.fileId ??
                  file.id ??
                  index;

                const fileName =
                  file.fileName ??
                  file.name ??
                  file.originalFileName ??
                  `Document ${index + 1}`;

                const filePath =
                  file.filePath ??
                  file.path ??
                  file.fileUrl ??
                  file.url ??
                  file.relativePath ??
                  "";

                const fileUrl =
                  filePath.startsWith("http")
                    ? filePath
                    : `${FILE_BASE_URL}${filePath}`;

                return (
                  <div
                    key={fileId}
                    className="border rounded-lg p-3 flex justify-between items-center gap-4"
                  >

                    <div className="min-w-0">

                      <div className="font-medium truncate">
                        {fileName}
                      </div>

                      {filePath && (
                        <div className="text-xs text-slate-500 truncate">
                          {filePath}
                        </div>
                      )}

                    </div>

                    {filePath ? (
                      <a
                        href={fileUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="shrink-0 text-blue-600 flex items-center gap-1"
                      >
                        <LinkIcon size={15} />
                        View
                      </a>
                    ) : (
                      <span className="text-xs text-red-500">
                        File path unavailable
                      </span>
                    )}

                  </div>
                );
              })}

            </div>
          )}

          {/* MARKS */}
          <div className="grid md:grid-cols-2 gap-5 mt-5">

            {/* TEACHER MARKS */}
            <div>
              <label className="text-xs font-semibold text-slate-500">
                Teacher Marks
              </label>

              <input
                type="number"
                value={teacherMarks}
                disabled
                className="w-full mt-1 border rounded-lg px-3 py-2 bg-slate-100 text-slate-700"
              />
            </div>

            {/* =========================================
        NORMAL SECTION
        ========================================= */}
            {!isSection24 ? (

              <div className="md:col-span-2">

                {isSuperAdmin ? (

                  <>
                    {/* TEACHER ORIGINAL MARKS */}
                    <div className="mb-4">
                      <label className="text-xs font-semibold text-slate-500">
                        Teacher Marks
                      </label>

                      <div className="mt-1 flex items-center justify-between border rounded-lg px-4 py-3 bg-slate-50">
                        <div>
                          <div className="text-sm font-semibold text-slate-700">
                            Teacher's Original Marks
                          </div>

                          <div className="text-[10px] text-slate-400">
                            Original marks awarded by teacher
                          </div>
                        </div>

                        <div className="text-lg font-bold text-slate-700">
                          {teacherMarks}
                          <span className="text-xs text-slate-400 font-medium">
                            {" "} / {q.maxMarks}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* ALL REVIEWERS — READ ONLY */}
                    <ReviewerReviews
                      reviews={reviewerReviews}
                      maxMarks={q.maxMarks}
                    />
                  </>

                ) : (

                  /* ADMIN / REVIEWER */
                  <div>
                    <label className="text-xs font-semibold text-slate-500">
                      Reviewed Marks
                    </label>

                    <input
                      type="number"
                      min={0}
                      max={q.maxMarks}
                      step="0.5"
                      value={reviewedMarks}
                      onChange={(e) =>
                        onMarks(
                          q.sectionId,
                          e.target.value
                        )
                      }
                      className="w-full mt-1 border rounded-lg px-3 py-2"
                    />
                  </div>

                )}

              </div>

            ) : (

              /* =========================================
                 SECTION 24
                 ========================================= */

              <div className="md:col-span-2">

                {isSuperAdmin ? (

                  /*
                   * SUPERADMIN SECTION 24
                   * READ ONLY
                   *
                   * IMPORTANT:
                   * This assumes displayOptions contains the
                   * reviewer marks that you want to display.
                   */
                  <div>

                    <label className="text-xs font-semibold text-slate-500 mb-2 block">
                      Option Wise Marks
                    </label>

                    <div className="text-xs text-slate-500 mb-3 bg-slate-50 border rounded-lg p-3">
                      प्रत्येक वर्षासाठी कमाल <b>5 गुण</b>.
                      मात्र या विभागाचे एकूण गुण कमाल{" "}
                      <b>{q.maxMarks}</b> राहतील.
                    </div>

                    <div className="space-y-3">

                      {displayOptions.map((option, index) => (

                        <div
                          key={
                            option.optionId ??
                            option.id ??
                            `section24-${index}`
                          }
                          className="border rounded-lg p-3 bg-white"
                        >

                          <div className="flex items-center justify-between gap-4">

                            <div className="flex-1">

                              <div className="text-xs text-slate-400 mb-1">
                                Year / Item {index + 1}
                              </div>

                              <div className="text-sm font-semibold">
                                {option.selectedOption ||
                                  `Year / Item ${index + 1}`}
                              </div>

                              {option.isEmpty && (
                                <div className="text-xs text-slate-400 mt-1">
                                  No response received
                                </div>
                              )}

                            </div>

                            {/* READ ONLY — NO INPUT */}
                            <div className="text-right">

                              <div className="text-sm font-bold text-blue-600">
                                {option.reviewedMarks ?? 0}
                                <span className="text-xs text-slate-400 font-medium">
                                  {" "} / 5
                                </span>
                              </div>

                              <div className="text-[9px] text-slate-400">
                                Read only
                              </div>

                            </div>

                          </div>

                        </div>

                      ))}

                    </div>

                    <div className="mt-3 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">

                      <div className="flex justify-between">

                        <span className="text-sm font-semibold text-blue-700">
                          Option Total
                        </span>

                        <span className="text-sm font-bold text-blue-700">
                          {optionMarksTotal}
                        </span>

                      </div>

                      <div className="flex justify-between mt-1">

                        <span className="text-sm font-semibold text-blue-700">
                          Section 24 Reviewed Marks
                        </span>

                        <span className="text-sm font-bold text-blue-700">
                          {section24ReviewedMarks} / {q.maxMarks}
                        </span>

                      </div>

                    </div>

                  </div>

                ) : (

                  /* ADMIN / REVIEWER — SECTION 24 EDITABLE */
                  <div>

                    <label className="text-xs font-semibold text-slate-500 mb-2 block">
                      Option Wise Marks
                    </label>

                    <div className="text-xs text-slate-500 mb-3 bg-slate-50 border rounded-lg p-3">
                      प्रत्येक वर्षासाठी कमाल <b>5 गुण</b>.
                      मात्र या विभागाचे एकूण गुण कमाल{" "}
                      <b>{q.maxMarks}</b> राहतील.
                    </div>

                    {displayOptions.map((option, index) => {

                      const optionMaxMarks = 5;

                      return (
                        <div
                          key={
                            option.optionId ??
                            option.id ??
                            `section24-${index}`
                          }
                          className="border rounded-lg p-3 mb-3 bg-white"
                        >

                          <div className="flex items-center justify-between gap-4">

                            <div className="flex-1">

                              <div className="text-xs text-slate-400 mb-1">
                                Year / Item {index + 1}
                              </div>

                              <div className="text-sm font-semibold">
                                {option.selectedOption ||
                                  `Year / Item ${index + 1}`}
                              </div>

                              {option.isEmpty && (
                                <div className="text-xs text-slate-400 mt-1">
                                  No response received
                                </div>
                              )}

                            </div>

                            <div className="flex items-center gap-2">

                              <input
                                type="number"
                                min={0}
                                max={optionMaxMarks}
                                step="0.5"
                                value={option.reviewedMarks ?? 0}
                                onChange={(e) =>
                                  onOptionMarks(
                                    q.sectionId,
                                    option.optionId,
                                    index,
                                    e.target.value
                                  )
                                }
                                className="w-24 border rounded-lg px-2 py-2"
                              />

                              <span className="text-xs text-slate-400">
                                / 5
                              </span>

                            </div>

                          </div>

                        </div>
                      );
                    })}

                    <div className="mt-3 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">

                      <div className="flex justify-between">

                        <span className="text-sm font-semibold text-blue-700">
                          Option Total
                        </span>

                        <span className="text-sm font-bold text-blue-700">
                          {optionMarksTotal}
                        </span>

                      </div>

                      <div className="flex justify-between mt-1">

                        <span className="text-sm font-semibold text-blue-700">
                          Section 24 Reviewed Marks
                        </span>

                        <span className="text-sm font-bold text-blue-700">
                          {section24ReviewedMarks} / {q.maxMarks}
                        </span>

                      </div>

                    </div>

                  </div>

                )}

              </div>

            )}

          </div>

          {/* VERIFY */}
          <div className="flex gap-3 mt-5">

            <button
              onClick={() =>
                onVerify(
                  q.sectionId,
                  true
                )
              }
              className={`flex items-center gap-2 px-4 py-2 rounded-lg ${q.isVerified
                ? "bg-green-600 text-white"
                : "border"
                }`}
            >
              <CheckCircle size={18} />
              Accept
            </button>

            <button
              onClick={() =>
                onVerify(
                  q.sectionId,
                  false
                )
              }
              className={`flex items-center gap-2 px-4 py-2 rounded-lg ${!q.isVerified
                ? "bg-red-600 text-white"
                : "border"
                }`}
            >
              <XCircle size={18} />
              Reject
            </button>

          </div>

          {/* REMARK */}
          <div className="mt-5">

            <label className="text-xs font-semibold text-slate-500">
              Remarks
            </label>

            <textarea
              value={q.remark ?? ""}
              onChange={(e) =>
                onRemark(
                  q.sectionId,
                  e.target.value
                )
              }
              className="w-full mt-2 border rounded-lg p-3"
              rows={4}
            />

          </div>

        </div>
      )}

    </div>
  );
}